import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { StatusGlyph } from '../StatusGlyph/StatusGlyph.js';
import { CERT_STAGES, CERT_STAGE_LABELS, LINEAR_STAGES, LOOP_STAGES, isCertStage, } from '../StageChip/stages.js';
const STATE_TEXT = {
    done: 'Done',
    active: 'In progress',
    upcoming: 'Not started',
    na: 'Not applicable',
};
/** Returns every C19 violation for a stations/openTab pair (empty = valid). */
export function stageRailProblems(stations, openTab) {
    const problems = [];
    const seen = new Map();
    for (const s of stations) {
        if (!isCertStage(s.stage))
            problems.push(`unknown stage "${String(s.stage)}"`);
        seen.set(s.stage, (seen.get(s.stage) ?? 0) + 1);
        if (s.state === 'na' && !s.naReason?.trim()) {
            problems.push(`station "${s.stage}" is N/A without a naReason`);
        }
    }
    for (const stage of CERT_STAGES) {
        const n = seen.get(stage) ?? 0;
        if (n === 0)
            problems.push(`missing station "${stage}"`);
        if (n > 1)
            problems.push(`station "${stage}" appears ${n} times`);
    }
    if (!isCertStage(openTab) || !seen.has(openTab)) {
        problems.push(`openTab "${String(openTab)}" must match exactly one station`);
    }
    return problems;
}
export function StageRail({ stations, openTab, compact = false, loopLabel = 'Certified', loopBack, label = 'Program stages', className, }) {
    const problems = stageRailProblems(stations, openTab);
    if (problems.length > 0) {
        throw new Error(`StageRail: ${problems.join('; ')} (Contract C19)`);
    }
    const byStage = new Map(stations.map((s) => [s.stage, s]));
    const pinned = CERT_STAGES.flatMap((stage) => (byStage.get(stage).periods ?? []).map((period) => ({ stage, period })));
    const renderStation = (stage, linear) => (_jsx(Station, { station: byStage.get(stage), current: stage === openTab, compact: compact, linear: linear }, stage));
    return (_jsxs("nav", { "aria-label": label, className: className, "data-stage-rail": true, "data-compact": compact ? '' : undefined, style: { fontFamily: 'var(--font-sans)', color: 'var(--text-muted)' }, children: [_jsxs("ol", { style: {
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) minmax(0, 2.3fr)',
                    gap: compact ? '2px' : 'var(--sp-2)',
                    alignItems: 'stretch',
                }, children: [LINEAR_STAGES.map((s) => renderStation(s, true)), _jsxs("li", { "data-stage-loop": true, style: {
                            minWidth: 0,
                            border: '1px solid var(--border-strong)',
                            borderRadius: 'var(--radius-lg)',
                            padding: compact ? '2px' : 'var(--sp-1) var(--sp-2) var(--sp-2)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--sp-1)',
                        }, children: [_jsxs("span", { style: {
                                    fontSize: compact ? '10px' : 'var(--fs-xs)',
                                    fontWeight: 700,
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    color: 'var(--text-muted)',
                                    padding: compact ? '0 4px' : '0 var(--sp-3)',
                                    display: 'flex',
                                    gap: 'var(--sp-2)',
                                    alignItems: 'center',
                                }, children: [_jsx("span", { children: loopLabel }), _jsx("span", { className: "sr-only", children: ": these stages run at the same time" })] }), _jsxs("ul", { style: {
                                    listStyle: 'none',
                                    margin: 0,
                                    padding: 0,
                                    display: 'grid',
                                    gridTemplateColumns: 'minmax(0, 1.25fr) minmax(0, 1fr)',
                                    gap: compact ? '2px' : 'var(--sp-1)',
                                    alignItems: 'stretch',
                                }, children: [renderStation(LOOP_STAGES[0], false), renderStation(LOOP_STAGES[1], false)] }), loopBack ? (_jsxs("span", { "data-stage-loop-back": true, style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: 'var(--fs-sm)',
                                    color: 'var(--text-muted)',
                                    padding: compact ? '0 4px' : '0 var(--sp-3)',
                                }, children: [_jsx("span", { "aria-hidden": "true", style: { color: 'var(--text-dim)' }, children: "\u21BB" }), _jsx("span", { className: compact ? 'sr-only' : undefined, children: loopBack })] })) : null] })] }), compact && pinned.length > 0 ? (_jsx("ul", { "data-stage-rail-periods": true, style: {
                    listStyle: 'none',
                    margin: 'var(--sp-2) 0 0',
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    fontSize: 'var(--fs-sm)',
                    color: 'var(--text-muted)',
                }, children: pinned.map(({ stage, period }) => (_jsxs("li", { style: { display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }, children: [_jsx(PinNode, {}), _jsxs("span", { children: [_jsx("b", { style: { color: 'var(--text)', fontWeight: 600 }, children: period }), " \u00B7 ", CERT_STAGE_LABELS[stage]] })] }, `${stage}:${period}`))) })) : null] }));
}
function PinNode() {
    return (_jsx("span", { "aria-hidden": "true", "data-part": "pin", style: {
            width: '14px',
            height: '14px',
            borderRadius: '999px',
            flex: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid var(--accent)',
            background: 'var(--surface-elevated)',
        }, children: _jsx("span", { style: { width: '4px', height: '4px', borderRadius: '999px', background: 'var(--accent)' } }) }));
}
function Node({ state, pinned }) {
    if (pinned)
        return _jsx(PinNode, {});
    const base = {
        width: '14px',
        height: '14px',
        borderRadius: '999px',
        flex: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--border-control)',
        background: 'transparent',
    };
    if (state === 'done') {
        return (_jsx("span", { "aria-hidden": "true", "data-part": "glyph", style: { ...base, background: 'var(--text-muted)', borderColor: 'var(--text-muted)', color: 'var(--surface-elevated)' }, children: _jsx(StatusGlyph, { kind: "check", size: 10 }) }));
    }
    if (state === 'active') {
        return (_jsx("span", { "aria-hidden": "true", "data-part": "glyph", style: { ...base, borderColor: 'var(--text)', color: 'var(--text)' }, children: _jsx(StatusGlyph, { kind: "half", size: 12 }) }));
    }
    if (state === 'na') {
        return _jsx("span", { "aria-hidden": "true", "data-part": "glyph", style: { ...base, borderStyle: 'dashed' } });
    }
    return _jsx("span", { "aria-hidden": "true", "data-part": "glyph", style: base });
}
function Station({ station, current, compact, linear, }) {
    const { stage, state, meta, href, naReason, periods = [] } = station;
    const name = CERT_STAGE_LABELS[stage];
    const pinned = periods.length > 0;
    const na = state === 'na';
    // A meta line that only repeats the state ("Done") stays visual: the sr-only state already says it (no "Done Done").
    const metaRepeatsState = typeof meta === 'string' && meta.trim().toLowerCase() === STATE_TEXT[state].toLowerCase();
    const shadows = [];
    if (pinned)
        shadows.push('inset 0 0 0 1px var(--accent)');
    if (current)
        shadows.push('inset 0 -3px 0 var(--text)');
    const cellStyle = {
        display: 'flex',
        flexDirection: 'column',
        gap: compact ? '2px' : '3px',
        height: '100%',
        minHeight: compact ? '44px' : undefined,
        padding: compact ? '6px 2px' : 'var(--sp-2) var(--sp-3) var(--sp-3)',
        alignItems: compact ? 'center' : undefined,
        justifyContent: compact ? 'center' : undefined,
        textAlign: compact ? 'center' : undefined,
        borderRadius: 'var(--radius-md)',
        color: current ? 'var(--text)' : na ? 'var(--text-dim)' : 'var(--text-muted)',
        background: current ? 'var(--accent-soft)' : 'transparent',
        boxShadow: shadows.length ? shadows.join(', ') : undefined,
        textDecoration: 'none',
    };
    const srState = [
        na ? `${STATE_TEXT.na}: ${naReason}` : STATE_TEXT[state],
        pinned ? `current period: ${periods.join(', ')}` : null,
    ]
        .filter(Boolean)
        .join('; ');
    const content = (_jsxs(_Fragment, { children: [_jsxs("span", { style: {
                    display: 'flex',
                    flexDirection: compact ? 'column' : 'row',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: compact ? '3px' : '4px var(--sp-2)',
                    minHeight: '20px',
                }, children: [_jsx(Node, { state: state, pinned: pinned }), _jsx("span", { style: {
                            fontWeight: current ? 700 : na ? 500 : 600,
                            fontSize: compact ? 'var(--fs-xs)' : 'var(--fs-md)',
                            color: na ? 'var(--text-muted)' : 'var(--text)',
                        }, children: name }), linear && !compact ? (_jsx("span", { "aria-hidden": "true", style: {
                            flex: '1 1 12px',
                            minWidth: '12px',
                            borderTop: state === 'done' ? '1px solid var(--text-muted)' : '1px dashed var(--border-control)',
                        } })) : null] }), _jsx("span", { className: "sr-only", children: srState }), !compact && pinned ? (_jsx("span", { "aria-hidden": "true", style: { fontSize: 'var(--fs-sm)', color: 'var(--text)', fontWeight: 600 }, children: periods.join(' · ') })) : null, meta ? (_jsx("span", { className: compact ? 'sr-only' : undefined, "aria-hidden": metaRepeatsState ? true : undefined, style: { fontSize: 'var(--fs-sm)', color: current ? 'var(--text)' : 'var(--text-muted)' }, children: meta })) : null, compact && state === 'done' && typeof meta === 'string' && meta.startsWith('Done · ') ? (_jsx("span", { "aria-hidden": "true", "data-done-periods": true, style: { fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', lineHeight: 1.2 }, children: meta.slice('Done · '.length) })) : null, na && !compact ? (_jsx("span", { "aria-hidden": "true", style: { fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }, children: naReason })) : null] }));
    return (_jsx("li", { "data-stage": stage, "data-state": state, "data-pinned": pinned ? '' : undefined, style: { minWidth: 0 }, children: href ? (_jsx("a", { href: href, "aria-current": current ? 'page' : undefined, style: cellStyle, children: content })) : (_jsx("div", { "aria-current": current ? 'step' : undefined, style: cellStyle, children: content })) }));
}
//# sourceMappingURL=StageRail.js.map